import type { Route } from "./+types/designers.ai";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { useT } from "~/i18n/context";
import { detectLocale, translations } from "~/i18n/index";
import type { Locale } from "~/i18n/index";
import { socialMeta } from "~/lib/seo";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = (data?.locale ?? "en") as Locale;
  const t = translations[locale] ?? translations.en;
  const m = t.designerAi.meta;
  return [
    { title: m.title },
    { name: "description", content: m.description },
    ...socialMeta({ title: m.title, description: m.socialDescription, pathname: "/designers/ai", locale }),
  ];
}

export default function DesignerAIPage() {
  const { t } = useT();
  const d = t.designerAi;

  return (
    <MarketingLayout>
      {/* ── Hero ── */}
      <section className="px-6 lg:px-12 pt-20 lg:pt-32 pb-16 lg:pb-24 relative overflow-hidden">
        <GridLines count={14} />

        <div className="relative max-w-5xl mx-auto">
          <span
            className="issue-label block mb-5"
            style={{ fontFamily: "var(--font-sans)", color: "var(--primary)" }}
          >
            {d.hero.overline}
          </span>

          <h1
            className="font-display font-light leading-[0.95] tracking-tight text-foreground"
            style={{ fontSize: "clamp(2.6rem, 6vw, 5rem)" }}
          >
            {d.hero.headingLine1}
            <br />
            <em className="not-italic" style={{ opacity: 0.42 }}>
              {d.hero.headingLine2}
            </em>
          </h1>

          <p
            className="text-muted-foreground leading-relaxed mt-8"
            style={{ fontSize: "1rem", maxWidth: "58ch" }}
          >
            {d.hero.body}
          </p>

          <div className="mt-10 flex flex-wrap gap-4 items-center">
            <a
              href="/cara/demo"
              className="inline-block px-8 py-4 transition-colors"
              style={{
                fontSize: "0.68rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                background: "var(--teal-sapphire)",
                color: "oklch(0.98 0 0)",
              }}
            >
              {d.hero.ctaDemo}
            </a>
            <a
              href="/sign-in"
              className="inline-block px-6 py-4 border border-foreground/20 hover:border-foreground/50 transition-colors text-foreground"
              style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
            >
              {d.hero.ctaStudio}
            </a>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ── Pillars ── */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-6xl mx-auto">
          <span
            className="issue-label block mb-5"
            style={{ fontFamily: "var(--font-sans)", color: "var(--primary)" }}
          >
            {d.pillars.label}
          </span>
          <h2
            className="font-display font-light leading-[1.05] tracking-tight text-foreground mb-14"
            style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
          >
            {d.pillars.headingLine1}
            <br />
            <em className="not-italic" style={{ opacity: 0.42 }}>
              {d.pillars.headingLine2}
            </em>
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-border">
            {d.pillars.items.map((pillar, i) => (
              <div key={pillar.label} className="relative">
                {i > 0 && (
                  <div className="hidden lg:block absolute inset-y-0 left-0 w-px bg-border" />
                )}
                <div className="p-8 lg:p-10 h-full flex flex-col">
                  <span
                    className="font-display font-light block mb-1"
                    style={{
                      fontSize: "clamp(1.6rem, 2.5vw, 2.2rem)",
                      lineHeight: 1,
                      color: "var(--primary)",
                    }}
                  >
                    {pillar.stat}
                  </span>
                  <span
                    className="issue-label block mb-4"
                    style={{ fontFamily: "var(--font-sans)", opacity: 0.5 }}
                  >
                    {pillar.label}
                  </span>
                  <div className="rule mb-5" />
                  <p
                    className="text-muted-foreground leading-relaxed flex-1"
                    style={{ fontSize: "0.82rem" }}
                  >
                    {pillar.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ── Capabilities ── */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-6xl mx-auto">
          <span
            className="issue-label block mb-5"
            style={{ fontFamily: "var(--font-sans)", color: "var(--primary)" }}
          >
            {d.capabilities.label}
          </span>
          <h2
            className="font-display font-light leading-[1.05] tracking-tight text-foreground mb-14"
            style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
          >
            {d.capabilities.headingLine1}
            <br />
            <em className="not-italic" style={{ opacity: 0.42 }}>
              {d.capabilities.headingLine2}
            </em>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0">
            {d.capabilities.items.map((cap, i) => (
              <div
                key={cap.label}
                className="py-6"
                style={{
                  paddingLeft: i % 3 !== 0 ? "2rem" : undefined,
                  paddingRight: i % 3 !== 2 ? "2rem" : undefined,
                  borderTop: "1px solid var(--border)",
                }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ background: "var(--primary)", opacity: 0.7 }}
                  />
                  <div>
                    <p className="text-sm font-medium text-foreground leading-snug">
                      {cap.label}
                    </p>
                    <p
                      className="text-muted-foreground leading-relaxed mt-1"
                      style={{ fontSize: "0.78rem" }}
                    >
                      {cap.detail}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ── Workflow Steps ── */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-5xl mx-auto">
          <span
            className="issue-label block mb-5"
            style={{ fontFamily: "var(--font-sans)", color: "var(--primary)" }}
          >
            {d.workflow.label}
          </span>
          <h2
            className="font-display font-light leading-[1.05] tracking-tight text-foreground mb-14"
            style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}
          >
            {d.workflow.heading}
          </h2>

          <div className="grid sm:grid-cols-2 gap-x-12 gap-y-0">
            {d.workflow.steps.map((step, i) => (
              <div
                key={step.title}
                className="py-8"
                style={{ borderTop: "1px solid var(--border)" }}
              >
                <div className="flex items-start gap-4">
                  <span
                    className="font-display font-light shrink-0"
                    style={{
                      fontSize: "2.4rem",
                      lineHeight: 1,
                      color: "var(--primary)",
                      opacity: 0.35,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3
                      className="text-foreground mb-2"
                      style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: "0.88rem",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                      }}
                    >
                      {step.title}
                    </h3>
                    <p
                      className="text-muted-foreground leading-relaxed"
                      style={{ fontSize: "0.82rem" }}
                    >
                      {step.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ── Closing statement ── */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div className="max-w-5xl mx-auto">
          <p
            className="font-display font-light italic text-foreground leading-relaxed"
            style={{
              fontSize: "clamp(1.05rem, 2vw, 1.4rem)",
              maxWidth: "56ch",
              lineHeight: 1.6,
            }}
          >
            {d.closing}
          </p>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ── Credits teaser ── */}
      <section className="px-6 lg:px-12 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto text-center">
          <span
            className="issue-label block mb-4"
            style={{ fontFamily: "var(--font-sans)", color: "var(--primary)" }}
          >
            {d.creditsTeaser.label}
          </span>
          <h2
            className="font-display font-light text-foreground"
            style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
          >
            {d.creditsTeaser.heading}
          </h2>
          <p
            className="text-muted-foreground mt-4 leading-relaxed mx-auto"
            style={{ fontSize: "0.92rem", maxWidth: "48ch" }}
          >
            {d.creditsTeaser.body}
          </p>
          <a
            href="/ai/credits"
            className="inline-block mt-6 transition-colors"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "0.75rem",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--teal-sapphire)",
              borderBottom: "1px solid var(--teal-sapphire)",
              paddingBottom: "0.15rem",
            }}
          >
            {d.creditsTeaser.link}
          </a>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ── Final CTA ── */}
      <section className="px-6 lg:px-12 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="font-display font-light text-foreground"
            style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
          >
            {d.finalCta.heading}
          </h2>
          <p
            className="text-muted-foreground mt-4 leading-relaxed mx-auto"
            style={{ fontSize: "0.92rem", maxWidth: "44ch" }}
          >
            {d.finalCta.body}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 items-center justify-center">
            <a
              href="/cara/demo"
              className="inline-block px-10 py-4 transition-colors"
              style={{
                fontSize: "0.68rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                background: "var(--teal-sapphire)",
                color: "oklch(0.98 0 0)",
              }}
            >
              {d.finalCta.ctaDemo}
            </a>
            <a
              href="/sign-in"
              className="inline-block px-8 py-4 border border-foreground/20 hover:border-foreground/50 transition-colors text-foreground"
              style={{
                fontSize: "0.68rem",
                letterSpacing: "0.2em",
                textTransform: "uppercase",
              }}
            >
              {d.finalCta.ctaStudio}
            </a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

function GridLines({ count }: { count: number }) {
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      {Array.from({ length: count }).map((_, i) => (
        <line
          key={i}
          x1={`${((i + 1) * 100) / (count + 1)}%`}
          y1="0"
          x2={`${((i + 1) * 100) / (count + 1)}%`}
          y2="100%"
          stroke="oklch(0.085 0.004 50)"
          strokeWidth="1"
          opacity="0.02"
        />
      ))}
    </svg>
  );
}
